// A FOLHA DE INSTRUCOES DOS 5 REPRESENTANTES, escrita UMA VEZ.
//
// Este arquivo tem so as PALAVRAS. Quem desenha sao dois renderizadores, e
// eles sao diferentes de proposito:
//   - src/paginas/Ajuda.tsx  -> a tela dentro do app, com a cara do app
//   - src/ajuda/gerarHtml.ts -> a pagina solta, que o representante abre no
//                               celular antes de instalar
//
// ⚠ Compartilha-se as STRINGS, nao o layout. Duas telas do mesmo dado que
// copiam o layout ficam parecidas e mentem em lugares diferentes; duas telas
// que copiam as palavras dizem a mesma coisa com a cara de cada lugar.
//
// ⚠ Mexeu numa frase aqui? `npm test` reprova ate a pagina solta ser
// regerada (`npm test -- -u`). A sincronia nao depende de ninguem lembrar.

import { WHATSAPP_DO_SUPORTE } from '../lib/suporte'

/** Um pedaco de texto dentro de um paragrafo. */
export type Trecho =
  | string
  /** Negrito. */
  | { forte: string }
  /** Texto que o representante vai LER NA TELA -- sai em fonte de maquina nos
   *  dois renderizadores, para ele saber que e a palavra exata a procurar.
   *
   *  Por padrao a frase e do PROPRIO APP, e ha guarda cobrando que ela exista
   *  mesmo no codigo (src/ajuda/citacoes.contrato.test.ts): guia que manda
   *  procurar botao inexistente e o pior defeito que um guia pode ter.
   *
   *  `de` marca a excecao: frase que aparece na tela mas NAO e do app --
   *  o menu do Chrome, o do Safari. O valor documenta a origem e desliga a
   *  guarda para aquela frase, que nao teria como ser encontrada no codigo. */
  | { tela: string; de?: string }
  /** Link que sai do app (pedido dela 18/10: "Suporte" abre o WhatsApp direto). */
  | { link: string; url: string }

export interface Luz {
  cor: 'verde' | 'amarelo' | 'vermelho'
  /** A frase EXATA da barra. Vem de sync/rotuloDaBarra.ts. */
  frase: string
  querDizer: string
}

/** ⚠ O flag mora no BLOCO, e nao na secao. O primeiro desenho o pos na secao
 *  e, para esconder um aviso de tres linhas, escondia a secao "Instalar no
 *  celular" inteira -- contra a decisao dela de 14/08 ("tudo, igual a pagina
 *  web"). Ha teste cobrando que some o bloco e nunca a secao. */
type Comum = { apenasNaPagina?: true }

export type Bloco = Comum &
  (
    | { tipo: 'paragrafo'; partes: Trecho[] }
    /** `ordenada` quando a lista E uma ordem que a pessoa segue de cima para
     *  baixo. Duas proibicoes sem ordem nenhuma ficam com marcador. */
    | { tipo: 'lista'; ordenada?: true; itens: Trecho[][] }
    | { tipo: 'subtitulo'; texto: string }
    | { tipo: 'endereco'; url: string; rotulo: string; legenda: string }
    | { tipo: 'atencao'; rotulo: string; blocos: Bloco[] }
    | { tipo: 'semaforo'; luzes: Luz[] }
  )

export interface Secao {
  titulo: string
  /** So onde existe SEQUENCIA de verdade: instalar, depois entrar. O resto e
   *  referencia para consultar no mes, e numerar aquilo inventaria uma ordem
   *  que ninguem segue. */
  passo?: number
  /** Divisor que abre um grupo de secoes. */
  parte?: string
  blocos: Bloco[]
  /** Pedido dela 18/10: o video do assunto vai junto da explicacao. Os 8 curtos
   *  publicos do site (catalogo em marketing/cerebro/videos.md). */
  videos?: Video[]
  /** Pedido dela 18/10: cada card aponta a aula do mesmo assunto na area de
   *  membros da Hotmart. So aula JA GRAVADA entra aqui. */
  aulas?: Aula[]
}

export interface Aula {
  modulo: string
  aula: string
}

export interface Video {
  titulo: string
  url: string
}

// Onde o comprador abre o curso: Minhas compras > Acessar. Trocar pelo link
// direto da area de membros quando ela passar.
export const AREA_DE_MEMBROS = 'https://consumer.hotmart.com/'

const VIDEO = (titulo: string, arquivo: string): Video => ({ titulo, url: `https://crmeunegocio.com.br/videos/${arquivo}.mp4` })

export interface Folha {
  marca: string
  titulo: string
  abertura: Trecho[][]
  secoes: Secao[]
  fecho: string
}

export const ENDERECO_DO_APP = 'https://app.crmeunegocio.com.br'

export const FOLHA: Folha = {
  marca: 'CRM Multimarca',
  titulo: 'Guia do representante',
  abertura: [
    [
      'Clientes, pedidos, comissões, metas e a rota do dia, tudo num app só. Ele funciona ',
      { forte: 'sem internet' },
      ' — foi feito para a rua.',
    ],
    [
      'Ficou com dúvida? Todos os vídeos tutoriais estão no curso, na Hotmart: ',
      { link: 'Abrir as aulas na Hotmart', url: AREA_DE_MEMBROS },
      '. Cada parte deste guia diz qual aula assistir.',
    ],
    ['Qualquer dúvida: ', { link: 'Suporte no WhatsApp', url: WHATSAPP_DO_SUPORTE }],
  ],

  secoes: [
    {
      parte: 'Comece por aqui',
      passo: 1,
      titulo: 'Instalar no celular',
      aulas: [{ modulo: 'Comece aqui', aula: 'Criar a conta e baixar o app' }, { modulo: 'Comece aqui', aula: 'Tour pelo app' }],
      blocos: [
        {
          tipo: 'endereco',
          url: ENDERECO_DO_APP,
          rotulo: 'app.crmeunegocio.com.br',
          legenda: `Endereço completo: ${ENDERECO_DO_APP}`,
        },
        {
          // ⚠ So na pagina solta: quem le a folha DENTRO do app ja instalou, e
          // um aviso sobre a janelinha do WhatsApp ali seria absurdo.
          apenasNaPagina: true,
          tipo: 'atencao',
          rotulo: 'Antes de tocar no endereço',
          blocos: [
            {
              tipo: 'paragrafo',
              partes: [
                'Se você está lendo isto ',
                { forte: 'dentro do WhatsApp' },
                ', o link abre numa janelinha do próprio WhatsApp — e ali não dá para instalar. ',
                'Copie o endereço e abra no navegador do celular: ',
                { forte: 'Chrome' },
                ' no Android, ',
                { forte: 'Safari' },
                ' no iPhone.',
              ],
            },
          ],
        },
        { tipo: 'subtitulo', texto: 'No Android' },
        {
          tipo: 'paragrafo',
          partes: [
            'Abra o endereço no ',
            { forte: 'Chrome' },
            ', toque nos três pontinhos ',
            { tela: '⋮', de: 'o menu do Chrome' },
            ' do canto e escolha ',
            { tela: 'Instalar aplicativo', de: 'o menu do Chrome' },
            ' (em alguns aparelhos aparece como ',
            { tela: 'Adicionar à tela inicial', de: 'o menu do Chrome' },
            ').',
          ],
        },
        { tipo: 'subtitulo', texto: 'No iPhone' },
        {
          tipo: 'paragrafo',
          partes: [
            'Abra o endereço no ',
            { forte: 'Safari' },
            ', toque no botão de compartilhar — o quadradinho com a seta para cima — e escolha ',
            { tela: 'Adicionar à Tela de Início', de: 'o menu do Safari' },
            '.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            { forte: 'No iPhone tem que ser pelo Safari mesmo.' },
            ' Pelo Chrome do iPhone essa opção não aparece.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Pronto: o CRM fica com ícone próprio na sua tela, igual a qualquer outro app. ',
            { forte: 'Use sempre pelo ícone' },
            ', não pelo navegador.',
          ],
        },
      ],
    },
    {
      passo: 2,
      titulo: 'Criar a sua conta',
      aulas: [{ modulo: 'Comece aqui', aula: 'Criar a conta e baixar o app' }],
      blocos: [
        {
          tipo: 'paragrafo',
          // Desde 15/10 a conta nasce da compra na Hotmart (cadastro aberto
          // desligado): o comprador recebe um convite por e-mail e cai na
          // tela de NovaSenha.tsx.
          partes: [
            'A sua conta nasce da assinatura. Depois da compra na Hotmart, chega no seu e-mail ',
            { forte: 'um convite para criar a sua senha' },
            '. Toque no link do e-mail: abre a tela ',
            { tela: 'Criar uma senha nova' },
            '.',
          ],
        },
        {
          tipo: 'lista',
          ordenada: true,
          itens: [
            [{ tela: 'Nova senha' }, ' — escolha uma sua, com pelo menos 8 caracteres.'],
            [{ tela: 'Repita a nova senha' }, ' — a mesma, de novo.'],
            ['Toque em ', { tela: 'Salvar senha' }, '.'],
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Vai aparecer ',
            { tela: 'Senha criada' },
            '. O link do e-mail abriu no navegador, e o app é outro lugar: agora ',
            { forte: 'abra o app pelo ícone da tela inicial' },
            ' e entre com o seu e-mail e a senha nova. Entra uma vez só: o app continua aberto nos próximos dias.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Se o convite não chegar em alguns minutos, olhe a caixa de spam. Se aparecer ',
            { tela: 'Link vencido' },
            ', peça um link novo pela tela de entrar, em ',
            { tela: 'Esqueci minha senha' },
            '.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Se um dia esquecer a senha, é o mesmo caminho: ',
            { tela: 'Esqueci minha senha' },
            ' manda um link para o seu e-mail. ',
            'Se o link não chegar nem na caixa de spam, fale com o suporte no WhatsApp.',
          ],
        },
        {
          tipo: 'atencao',
          rotulo: 'O seu espaço é só seu',
          blocos: [
            {
              tipo: 'paragrafo',
              partes: [
                'Cada conta tem os próprios clientes, produtos, pedidos e comissões, e o sistema guarda cada conta ',
                'separada das outras: ',
                { forte: 'nenhum outro representante e nenhuma fornecedora conseguem ver o que é seu' },
                ' — e você também não vê o de ninguém. A ligação entre o seu celular e o servidor é ',
                'protegida (criptografada), como a do aplicativo do banco, e os dados ficam guardados ',
                'em servidor no Brasil. Os seus dados são seus: não vendemos nem passamos para ninguém.',
              ],
            },
            {
              tipo: 'paragrafo',
              partes: [
                'E a lei está do seu lado. A ',
                { forte: 'Lei Geral de Proteção de Dados (LGPD, Lei 13.709/2018)' },
                ' garante o seu direito de saber quais dados seus estão guardados, de corrigir e de pedir que ',
                'sejam apagados — é só falar com o suporte. O ',
                { forte: 'Marco Civil da Internet (Lei 12.965/2014)' },
                ' também protege a privacidade das suas informações.',
              ],
            },
            {
              tipo: 'paragrafo',
              partes: ['O app começa vazio de propósito — os próximos três passos são para você enchê-lo.'],
            },
          ],
        },
      ],
    },

    {
      parte: 'Montar o seu app',
      passo: 3,
      titulo: 'Cadastrar as suas fornecedoras',
      aulas: [{ modulo: 'Sua fornecedora', aula: 'Cadastrar a fornecedora' }, { modulo: 'Sua fornecedora', aula: 'Condições de pagamento e comissão' }],
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            'Comece pelas marcas que você representa — sem elas não dá para ter produto nem preço. ',
            'Vá em ',
            { tela: 'Mais' },
            ' → ',
            { tela: 'Fornecedoras' },
            ' → ',
            { tela: '+ Nova' },
            '.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: ['Só o ', { tela: 'Nome' }, ' é obrigatório. O resto ajuda depois:'],
        },
        {
          tipo: 'lista',
          itens: [
            [
              { tela: 'Pedido mínimo (R$)' },
              ' — o app avisa na hora de fechar se o pedido não chegou lá.',
            ],
            [{ tela: 'Prazo de entrega (dias)' }, ' e ', { tela: 'Condições de pagamento' }, '.'],
            [
              { tela: 'Transportadoras' },
              ' (separadas por vírgula) — viram opções na hora de fechar o pedido.',
            ],
            [
              { tela: 'Tabelas de preço' },
              ' — pode deixar em branco agora. O passo 4 cria as tabelas sozinho, e é o jeito mais seguro.',
            ],
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [{ tela: 'Salvar' }, '. Repita para cada marca sua.'],
        },
      ],
    },

    {
      passo: 4,
      titulo: 'Trazer os produtos e os preços',
      aulas: [{ modulo: 'Produtos e preços', aula: 'Como preencher a planilha de produtos' }, { modulo: 'Produtos e preços', aula: 'Importar a tabela de preços' }, { modulo: 'Produtos e preços', aula: 'Cadastrar produto à mão' }],
      videos: [VIDEO('Importe sua tabela', '01-importar-planilha'), VIDEO('Produto novo na visita? Cadastre na hora', '02-cadastrar-produto')],
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            'Vá em ',
            { tela: 'Mais' },
            ' → ',
            { tela: 'Importar planilha' },
            '. Em ',
            { tela: 'O que importar?' },
            ', escolha ',
            { tela: 'Produtos e preços' },
            '.',
          ],
        },
        {
          tipo: 'lista',
          ordenada: true,
          itens: [
            [
              'Toque em ',
              { tela: 'Baixar modelo de' },
              ' Produtos e preços. ',
              { forte: 'Use o modelo' },
              ' — ele já vem com as colunas certas, e é o que evita quase todo erro de importação.',
            ],
            ['Preencha o modelo com a lista da fornecedora e salve no celular.'],
            ['Escolha a ', { tela: 'Fornecedora' }, ' — a mesma que você cadastrou no passo 3.'],
            [
              'Toque em ',
              { tela: 'Escolher arquivo' },
              ' e busque o arquivo. Ele aceita planilha ',
              { forte: '.xlsx' },
              ', ',
              { forte: '.csv' },
              ' — e também ',
              { forte: 'PDF' },
              '.',
            ],
            [
              'Confira as colunas que ele reconheceu, corrija o que estiver trocado, e mande aplicar.',
            ],
          ],
        },
        {
          tipo: 'atencao',
          rotulo: 'Deixe a importação criar a tabela de preços',
          blocos: [
            {
              tipo: 'paragrafo',
              partes: [
                'Se a tabela já existir e estiver ',
                { forte: 'sem regra de comissão' },
                ', a importação passa direto sem avisar — e depois esses produtos vendem pagando ',
                { forte: '0% de comissão' },
                '. Na primeira importação de uma marca, a tabela e a regra nascem juntas e certas. ',
                'Por isso: não crie a tabela na mão antes de importar.',
              ],
            },
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'O PDF é lido por inteligência artificial e ',
            { forte: 'precisa de internet' },
            ' — funciona bem até mais ou menos 5 MB e 500 linhas. Lista maior que isso, ou você sem sinal: use a planilha.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Depois de importar, confira em ',
            { tela: 'Mais' },
            ' → ',
            { tela: 'Produtos' },
            ': cada produto tem de aparecer com o preço e o nome da tabela. ',
            'Produto sem preço não aparece na busca do pedido.',
          ],
        },
      ],
    },

    {
      passo: 5,
      titulo: 'Cadastrar os seus clientes',
      aulas: [{ modulo: 'Clientes', aula: 'Como preencher a planilha de clientes' }, { modulo: 'Clientes', aula: 'Importar a planilha de clientes' }, { modulo: 'Clientes', aula: 'Cadastrar cliente à mão' }],
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            'Um por um na aba ',
            { tela: 'Clientes' },
            ' → ',
            { tela: '+ Novo' },
            '; ou todos de uma vez pela mesma tela de importar, escolhendo ',
            { tela: 'Clientes' },
            ' em ',
            { tela: 'O que importar?' },
            '.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Dá para salvar um cliente só com o nome e completar depois. Mas duas coisas valem preencher já:',
          ],
        },
        {
          tipo: 'lista',
          itens: [
            [
              { forte: 'CNPJ, DDD e telefone' },
              ' — sem eles a fornecedora não emite nota. O próprio cliente avisa: ',
              { tela: 'Cadastro incompleto.' },
            ],
            [
              { forte: 'Dia de visita' },
              ' — é o que põe o cliente na sua agenda. Sem ele o app avisa ',
              { tela: 'Cliente sem rota.' },
              ' e o cliente não aparece em dia nenhum.',
            ],
          ],
        },
      ],
    },

    {
      parte: 'O dia a dia',
      titulo: 'A rota da semana e a rota do dia',
      aulas: [{ modulo: 'Rota de visitas', aula: 'Montar a rota da semana' }, { modulo: 'Rota de visitas', aula: 'A rota do dia' }],
      videos: [VIDEO('Quem visitar hoje? Organize sua rota', '05-rota-do-dia'), VIDEO('Compromissos com hora marcada', '04-compromissos')],
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            'Aba ',
            { tela: 'Rota' },
            ' → ',
            { tela: 'Montar rota da semana' },
            '. Você diz em que dia visita cada cliente, e isso monta a sua semana inteira de uma vez.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Toque num dia do topo para ver só os clientes dele. Para dar o mesmo dia a vários clientes, marque a caixinha de cada um e use ',
            { tela: 'Definir dia ›' },
            ' na barra de baixo.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Feito isso, a aba ',
            { tela: 'Rota' },
            ' abre todo dia já na lista de quem você visita hoje, e vai contando quantos você já visitou.',
          ],
        },
      ],
    },

    {
      titulo: 'Fazer um pedido',
      aulas: [{ modulo: 'Pedidos', aula: 'Fazer um pedido' }],
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            'O pedido nasce ',
            { forte: 'no cliente' },
            ', não na aba de pedidos. Na ',
            { tela: 'Rota' },
            ', no cliente que você está visitando, toque em ',
            { tela: 'Novo pedido' },
            '. Ou abra o cliente pela aba ',
            { tela: 'Clientes' },
            ' e toque em ',
            { tela: '🛒 Novo pedido' },
            '.',
          ],
        },
        {
          tipo: 'atencao',
          rotulo: 'Não procure um botão de novo pedido na aba Pedidos',
          blocos: [
            {
              tipo: 'paragrafo',
              partes: [
                'A aba ',
                { tela: 'Pedidos' },
                ' é a lista do que você já fez, para acompanhar e filtrar. Ela não abre pedido novo — quem abre é o cliente.',
              ],
            },
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Dentro do pedido, use ',
            { tela: 'Buscar produto para adicionar (mín. 2 letras)' },
            ': digite o código ou parte do nome, e toque no resultado para incluir. ',
            'Cada linha mostra a fornecedora, a tabela e o preço — se o produto tiver duas tabelas, aparece uma linha por tabela e você escolhe qual está vendendo.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Na linha do item você ajusta a quantidade e, se precisar, o desconto. O ',
            { tela: 'Total do pedido' },
            ' fica no pé, sempre atualizado.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            { forte: 'Um pedido só pode ter várias marcas.' },
            ' Ponha tudo o que o cliente comprou, de todas as fornecedoras: o app separa por marca sozinho na hora de gerar os documentos.',
          ],
        },
      ],
    },

    {
      titulo: 'Fechar e enviar',
      aulas: [{ modulo: 'Pedidos', aula: 'Enviar o pedido' }, { modulo: 'Pedidos', aula: 'Acompanhar o pedido' }],
      videos: [VIDEO('Um pedido, várias marcas', '07-enviar-o-pedido'), VIDEO('Acompanhe o status do pedido', '06-pedido-status-e-mudancas')],
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            'Quando terminar de montar, toque em ',
            { tela: 'Fechar pedido' },
            ' e preencha as condições:',
          ],
        },
        {
          tipo: 'lista',
          itens: [
            [{ tela: 'Condição de pagamento' }],
            [{ tela: 'Forma de pagamento' }, ' — aparece quando a fornecedora tem formas ativas, e aí é obrigatória.'],
            [
              { tela: 'Frete' },
              ' — ',
              { tela: 'CIF (pago pela fábrica)' },
              ' ou ',
              { tela: 'FOB (pago pelo cliente)' },
            ],
            [{ tela: 'Transportadora' }, ' e ', { tela: 'Previsão de entrega' }],
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Se faltar chegar no mínimo de alguma marca, ele avisa com ',
            { tela: 'Pedido mínimo não atingido' },
            ' antes de deixar você seguir.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Depois é ',
            { tela: 'Confirmar e gerar PDFs' },
            '. O app monta um documento por fornecedora, mais um ',
            { tela: 'Resumo para o cliente' },
            ' e a opção ',
            { tela: 'Todas as fornecedoras juntas' },
            '.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            { forte: 'Confira a prévia antes de enviar.' },
            ' O app mostra como o documento vai ficar; só depois que você mandar é que ele sai. ',
            'Olhar dois segundos ali evita mandar pedido errado para a fábrica.',
          ],
        },
      ],
    },

    {
      titulo: 'Comissões',
      videos: [VIDEO('Confira suas comissões', '08-comissoes-e-baixa')],
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            { tela: 'Mais' },
            ' → ',
            { tela: 'Comissões' },
            '. Escolha o mês e entre na fornecedora para conferir o que ela te deve e dar baixa no que já caiu.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'O ',
            { tela: 'A receber no mês' },
            ' do topo, e o cartão de comissões da tela de abertura, andam sozinhos conforme os pedidos vão virando faturamento.',
          ],
        },
      ],
    },

    {
      titulo: 'Indique e ganhe',
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            { tela: 'Mais' },
            ' → ',
            { tela: 'Indique e ganhe até 20%' },
            '. Vire afiliado do CRM na Hotmart, cole o seu link de afiliado no app e use ',
            { tela: 'Compartilhar no WhatsApp' },
            '. Se a pessoa assinar pelo seu link, você recebe 20% do plano anual ou 5% do plano mensal, pago pela Hotmart, depois dos 30 dias de teste grátis.',
          ],
        },
      ],
    },

    {
      titulo: 'Metas',
      videos: [VIDEO('Quanto falta para a meta?', '03-metas')],
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            { tela: 'Mais' },
            ' → ',
            { tela: 'Metas' },
            '. São três jeitos de criar, e você escolhe o que preferir:',
          ],
        },
        {
          tipo: 'lista',
          itens: [
            [{ tela: 'Criar meta' }, ' — uma meta avulsa, do jeito que você quiser.'],
            [
              { tela: 'Criador' },
              ' — o app olha o seu histórico e sugere metas a partir do que você já vendeu.',
            ],
            [{ tela: 'Repetir' }, ' — traz as metas do mês passado para este mês.'],
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'O andamento aparece na tela de abertura, e a barra anda sozinha conforme os pedidos chegam. ',
            'Não precisa sair da tela e voltar.',
          ],
        },
      ],
    },

    {
      parte: 'Enquanto você usa',
      titulo: 'A barra de cima é o seu semáforo',
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            'Ela aparece em todas as telas e responde uma pergunta só: o que eu fiz já subiu ',
            'para o servidor?',
          ],
        },
        {
          // ⚠ As quatro frases sao as de src/sync/rotuloDaBarra.ts, copiadas.
          // Folha que descreve a tela com outras palavras vira a copia que
          // ninguem atualiza.
          tipo: 'semaforo',
          luzes: [
            {
              cor: 'verde',
              frase: 'Sincronizado',
              querDizer: 'Está tudo salvo no servidor. Pode ficar tranquilo.',
            },
            {
              cor: 'amarelo',
              frase: '3 esperando',
              querDizer: 'Três coisas ainda vão subir. É normal — ele tenta sozinho.',
            },
            {
              cor: 'amarelo',
              frase: 'Offline · 3 esperando',
              querDizer: 'Você está sem sinal. Está tudo guardado no aparelho.',
            },
            {
              cor: 'vermelho',
              frase: '2 não subiram',
              querDizer: 'Duas coisas foram recusadas pelo servidor. Fale com o suporte.',
            },
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Dentro de um pedido tem o mesmo aviso, só daquele pedido: ',
            { tela: 'Salvo no aparelho · aguardando envio' },
            '.',
          ],
        },
      ],
    },
    {
      titulo: 'Trabalhar sem sinal pode',
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            'Montar pedido, cadastrar cliente e fazer check-in de visita funcionam ',
            { forte: 'sem internet nenhuma' },
            '. Fica tudo guardado no celular e sobe sozinho quando o sinal voltar.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: ['Se quiser empurrar na hora, é só ', { forte: 'tocar na barra de cima' }, '.'],
        },
      ],
    },
    {
      titulo: 'Duas coisas para não fazer',
      blocos: [
        {
          tipo: 'atencao',
          rotulo: 'Com a barra amarela ou vermelha',
          blocos: [
            {
              tipo: 'lista',
              itens: [
                [
                  { forte: 'Não desinstale o app.' },
                  ' O que ainda não subiu está só no seu celular — desinstalar apaga.',
                ],
                [{ forte: 'Não limpe os dados do navegador' }, ', pelo mesmo motivo.'],
              ],
            },
            {
              tipo: 'paragrafo',
              partes: ['Com a barra verde, pode desinstalar à vontade: está tudo no servidor.'],
            },
          ],
        },
      ],
    },
    {
      titulo: 'Quando sair uma atualização',
      blocos: [
        {
          tipo: 'paragrafo',
          partes: [
            'O app se atualiza sozinho, mas ',
            { forte: 'só quando você fecha e abre' },
            '.',
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Fechar de verdade é deslizar o app para fora da lista de aplicativos recentes — ',
            'minimizar não basta. Depois abra de novo. ',
            { forte: 'Às vezes precisa abrir duas vezes.' },
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Para conferir qual versão você está usando: na tela de ',
            { forte: 'Início' },
            ', lá embaixo, aparece a palavra ',
            { tela: 'versão' },
            ' e um código. Esse código é a resposta.',
          ],
        },
      ],
    },
    {
      titulo: 'Deu problema? Mande assim',
      blocos: [
        { tipo: 'paragrafo', partes: ['Quanto mais completo, mais rápido a gente resolve:'] },
        {
          tipo: 'lista',
          ordenada: true,
          itens: [
            ['O que você estava fazendo — "fechando o pedido do cliente X"'],
            ['O que apareceu na tela — um print ajuda muito'],
            ['O código da versão, o do rodapé da tela de Início'],
            ['Se estava com sinal ou sem'],
          ],
        },
        {
          tipo: 'paragrafo',
          partes: [
            'Não perca tempo tentando arrumar: anote, mande para o suporte no WhatsApp e siga trabalhando. ',
            { forte: 'Nada do que você já fez se perde.' },
          ],
        },
      ],
    },
  ],

  fecho: 'Bom trabalho na rua — e boas vendas.',
}
